import { orderItemKey } from '../../../../utils/orderItemKeys';
import React, { useEffect, useRef } from 'react';
import OrderItemDesktop from '../../OrderItemDesktop';
import OrderItemMobile from '../../OrderItemMobile';
import { isScannerPickButtonAvailable } from './adminOrderScannerPickUtils';

/**
 * Список товарів замовлення + коментарі + підсумок. Один компонент для десктопу та мобайлу.
 * variant: 'desktop' | 'mobile' — вибір OrderItemDesktop або OrderItemMobile та розкладки коментарів.
 * Bulk-вибір працює для обох розкладок.
 */
const OrderItemsList = ({
  order,
  variant,
  setModalImage,
  handleEditClick,
  renderUserComment,
  renderAdminComment,
  onOpenScannerPick,
  selectedOrderItemKeys,
  onToggleSelect,
  onSelectAll,
}) => {
  const selectAllCheckboxRef = useRef(null);

  const isDesktop = variant === 'desktop';
  const isSelectable = Boolean(onToggleSelect);
  const ItemComponent = isDesktop ? OrderItemDesktop : OrderItemMobile;
  const orderItems = order?.orderItems || [];
  const orderItemKeys = orderItems.map((item) => orderItemKey(item));
  const isAllSelected = isSelectable && orderItemKeys.length > 0 && orderItemKeys.every((id) => selectedOrderItemKeys?.includes(id));
  const isSomeSelected = isSelectable && orderItemKeys.some((id) => selectedOrderItemKeys?.includes(id));

  useEffect(() => {
    const ref = selectAllCheckboxRef.current;
    if (!ref) return;
    ref.indeterminate = isSomeSelected && !isAllSelected;
  }, [isSomeSelected, isAllSelected]);

  if (!order || !order.orderItems) return null;

  /** Зверху списку (лише десктоп): outline-кнопка. На мобільному збірка — одна кнопка під карткою в AdminOrderMobileView. */
  const showScannerRowTop =
    isDesktop &&
    orderItems.length > 0 &&
    Boolean(onOpenScannerPick) &&
    order.pickedWithScanner !== true &&
    isScannerPickButtonAvailable(order);

  return (
    <>
      {showScannerRowTop ? (
        <div className={isDesktop ? 'mb-2' : 'mb-2 px-1'}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onOpenScannerPick}>
            <i className="bi bi-upc-scan mr-1" aria-hidden="true" />
            Зібрати сканером
          </button>
        </div>
      ) : null}

      {isDesktop && orderItems.length > 0 && (
        <div className="d-flex align-items-center border-bottom pb-2">
          {onToggleSelect && (
            <label
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0 mr-2"
              style={{ width: '28px', height: '28px', minWidth: '28px', cursor: 'pointer' }}
              title="Обрати всі"
            >
              <input
                ref={selectAllCheckboxRef}
                type="checkbox"
                className="m-0"
                checked={isAllSelected}
                onChange={onSelectAll}
                aria-label="Обрати всі товари"
                style={{ width: '1rem', height: '1rem', cursor: 'pointer', position: 'static', margin: 0 }}
              />
            </label>
          )}
          <div className="flex-grow-1 min-width-0">
            <div className="row align-items-center font-weight-bold small text-muted">
              <div className="col-1">Фото</div>
              <div className="col-5">Товар</div>
              <div className="col-1">Кількість</div>
              <div className="col-2">Ціна</div>
              <div className="col-2 text-center">Статус</div>
              <div className="col-1 text-center">Дії</div>
            </div>
          </div>
        </div>
      )}

      {orderItems.map((item, index) => (
        <ItemComponent
          key={orderItemKey(item)}
          item={item}
          setModalImage={setModalImage}
          handleEditClick={handleEditClick}
          isLast={!isDesktop && index === orderItems.length - 1}
          selected={isSelectable ? selectedOrderItemKeys?.includes(orderItemKey(item)) : undefined}
          onToggleSelect={onToggleSelect}
        />
      ))}

      {isDesktop ? (
        <div className="row mx-0">
          <div className="col-6 pt-3">{renderUserComment && renderUserComment()}</div>
          <div className="col-6 pt-3">{renderAdminComment && renderAdminComment()}</div>
        </div>
      ) : null}
    </>
  );
};

export default OrderItemsList;
